import LoadingScreen from '@/components/LoadingScreen';
import { formatChallengeDay } from '@/components/DateSelector';
import { CHALLENGE_END, CHALLENGE_START } from '@/constants/challenge';
import { getTestAppForDate, getTestAppUrl, type TestApp } from '@/data/testApps';
import { useAppTests } from '@/hooks/useAppTests';
import { useOpenedApps } from '@/hooks/useOpenedApps';
import * as Haptics from 'expo-haptics';
import * as WebBrowser from 'expo-web-browser';
import { useQuiz } from "@/hooks/useQuiz";
import { useUserScores } from "@/hooks/useUserScores";
import { showAlert } from "@/utils/showAlert";
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar } from 'expo-status-bar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from "react-native";
import ConfettiCannon from 'react-native-confetti-cannon';
import { SafeAreaView } from "react-native-safe-area-context";

// Points crédités pour avoir testé une app partenaire.
const TEST_APP_POINTS = 10;

export default function QuizScreen() {
  const { t, i18n } = useTranslation();

  // « Quiz du lun. 14 septembre » : en FR, minuscule initiale pour la lecture
  // en milieu de phrase ; en EN, le libellé garde sa capitale (« Mon 14 September »).
  const quizDayLabel = (challengeDate: string): string => {
    const label = formatChallengeDay(challengeDate);
    return i18n.language?.startsWith('fr')
      ? label.charAt(0).toLowerCase() + label.slice(1)
      : label;
  };

  const {
    questions,
    loading,
    submitAnswer,
    getTodayChallengeDate,
    isQuestionUnlocked,
    getAnswerForDate,
    getCorrectionForDate,
  } = useQuiz();
  const { addPoints } = useUserScores();
  const { testedAppIds, markAsTested } = useAppTests();
  const { openedAppIds, markOpened } = useOpenedApps();

  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Confettis : remonte `confettiKey` à chaque déclenchement pour relancer
  // le canon (la clé force le remontage du composant).
  const [confettiKey, setConfettiKey] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const fireConfetti = () => {
    setConfettiKey((k) => k + 1);
    setShowConfetti(true);
  };

  // « Aujourd'hui » dans le calendrier du challenge (mode test inclus).
  const todayDate = getTodayChallengeDate();

  // Restore answered state when a question is expanded. Les options
  // affichées viennent directement de question.options : elles sont
  // mélangées CÔTÉ SERVEUR dans un ordre stable (la bonne réponse n'est pas
  // identifiable avant de répondre).
  useEffect(() => {
    if (expandedQuestion) {
      const question = questions.find(q => q.id === expandedQuestion);
      if (question) {
        // Check if already answered
        const answer = getAnswerForDate(question.challengeDate);
        if (answer) {
          setShowFeedback(true);
          setIsCorrect(answer.isCorrect);
          setPointsEarned(answer.pointsEarned);
        }
      }
    }
  }, [expandedQuestion, questions, getAnswerForDate]);

  // Auto-expand today's question when questions load
  useEffect(() => {
    if (questions.length > 0 && expandedQuestion === null) {
      const todayQuestion = questions.find(q => q.challengeDate === todayDate);
      if (todayQuestion) {
        setExpandedQuestion(todayQuestion.id);
      }
    }
  }, [questions, todayDate, expandedQuestion]);

  const handleExpandQuestion = (questionId: string, challengeDate: string) => {
    // Only expand if it's today's question
    if (!isQuestionUnlocked(challengeDate)) return;

    const isToday = challengeDate === todayDate;
    
    if (expandedQuestion === questionId) {
      // Prevent closing today's question
      if (isToday) return;
      setExpandedQuestion(null);
      resetState();
    } else {
      setExpandedQuestion(questionId);
      resetState();
    }
  };

  const resetState = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setPointsEarned(0);
  };

  const handleValidate = async () => {
    const question = questions.find(q => q.id === expandedQuestion);
    if (!selectedAnswer || !question || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Correction 100 % côté serveur : on envoie le texte affiché, le
      // serveur corrige et renvoie is_correct/points/correction.
      const result = await submitAnswer(
        question.id,
        selectedAnswer,
        question.challengeDate
      );

      if (result.success) {
        setShowFeedback(true);
        setIsCorrect(result.isCorrect);
        setPointsEarned(result.pointsEarned);

        if (result.isCorrect) {
          // addPoints est l'unique source de crédit (DB + état local).
          await addPoints(result.pointsEarned, false);
          fireConfetti();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // « J'ai testé » : crédite les points + confettis, une seule fois par app.
  const handleMarkTested = async (appId: string, kind: TestApp['kind']) => {
    const result = await markAsTested(appId);
    if (result.success && !result.alreadyTested) {
      await addPoints(TEST_APP_POINTS, false);
      fireConfetti();
      showAlert(
        t('quiz.testedAlertTitle'),
        t(kind === 'site' ? 'quiz.visitedAlertMessage' : 'quiz.testedAlertMessage', {
          points: TEST_APP_POINTS,
        })
      );
    }
  };

  const getQuestionState = (challengeDate: string): 'locked' | 'available' | 'correct' | 'incorrect' => {
    const answer = getAnswerForDate(challengeDate);
    if (answer) {
      return answer.isCorrect ? 'correct' : 'incorrect';
    }
    if (isQuestionUnlocked(challengeDate)) {
      return 'available';
    }
    return 'locked';
  };

  const getStateIcon = (state: string) => {
    switch (state) {
      case 'correct':
        return <Ionicons name="checkmark-circle" size={20} color="#83BF4F" />;
      case 'incorrect':
        return <Ionicons name="close-circle" size={20} color="#E74C3C" />;
      case 'available':
        return <Ionicons name="help-circle-outline" size={20} color="#CD7728" />;
      case 'locked':
        return <Ionicons name="lock-closed-outline" size={20} color="#D4D6DD" />;
    }
  };

  // Conditions séparées : `loading` seul contrôle le spinner. L'ancienne
  // condition `loading || !question` bloquait l'écran sur le spinner à vie
  // quand aucune question n'existait pour aujourd'hui ou que le fetch
  // échouait (l'état vide était inatteignable).
  if (loading) {
    return (
      <View style={styles.container}>
        <StatusBar style="dark" />
        <LoadingScreen message={t('quiz.loading')} />
      </View>
    );
  }

  if (questions.length === 0) {
    return (
      <SafeAreaView style={styles.container} edges={["top"]}>
        <StatusBar style="dark" />
        <View style={styles.emptyContainer}>
          <Ionicons name="calendar" size={64} color="#CD7728" />
          <Text style={styles.emptyTitle}>{t('quiz.emptyTitle')}</Text>
          <Text style={styles.emptyText}>{t('quiz.emptyText')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  // 12 jours du challenge triés par date (lun. 14 → ven. 25).
  const sortedQuestions = [...questions].sort((a, b) =>
    a.challengeDate.localeCompare(b.challengeDate)
  );

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <StatusBar style="dark" />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.title}>{t('quiz.title')}</Text>
          <Text style={styles.subtitle}>{t('quiz.subtitle')}</Text>
        </View>

        {(() => {
          // Week-end (19–20 sept.) : ni quiz ni ressource, par demande client.
          // Sans cette carte l'écran n'afficherait RIEN pour aujourd'hui —
          // l'utilisateur croirait à un bug plutôt qu'à une pause voulue.
          const hasQuestionToday = questions.some(q => q.challengeDate === todayDate);
          const hasAppToday = !!getTestAppForDate(todayDate);
          const inChallenge = todayDate >= CHALLENGE_START && todayDate <= CHALLENGE_END;
          if (hasQuestionToday || hasAppToday || !inChallenge) return null;
          return (
            <View style={styles.missionsDoneCard}>
              <Text style={styles.missionsDoneEmoji}>🌿</Text>
              <Text style={styles.missionsDoneTitle}>{t('quiz.weekendTitle')}</Text>
              <Text style={styles.missionsDoneText}>{t('quiz.weekendText')}</Text>
            </View>
          );
        })()}

        {(() => {
          // Mode félicitation #17 : aujourd'hui le quiz est répondu ET
          // l'app du jour a été testée. Affichage d'un encart de réussite.
          const todayAnswer = getAnswerForDate(todayDate);
          const todayApp = getTestAppForDate(todayDate);
          const todayAppTested = todayApp
            ? testedAppIds.has(todayApp.id)
            : true;
          const allTodayMissionsDone = !!todayAnswer && todayAppTested;
          if (!allTodayMissionsDone) return null;
          return (
            <View style={styles.missionsDoneCard}>
              <Text style={styles.missionsDoneEmoji}>🎯</Text>
              <Text style={styles.missionsDoneTitle}>{t('quiz.allDoneTitle')}</Text>
              <Text style={styles.missionsDoneText}>{t('quiz.allDoneText')}</Text>
            </View>
          );
        })()}

        {sortedQuestions.map((question) => {
          const state = getQuestionState(question.challengeDate);
          const isExpanded = expandedQuestion === question.id;
          const isToday = question.challengeDate === todayDate;
          const answer = getAnswerForDate(question.challengeDate);
          // Correction serveur (bonne réponse + explication) : n'existe
          // QU'APRÈS avoir répondu — avant, l'app ne connaît pas la réponse.
          const correction = getCorrectionForDate(question.challengeDate);

          return (
            <TouchableOpacity
              key={question.id}
              style={[
                styles.questionCard,
                state === 'locked' && styles.questionCardLocked,
                isToday && styles.questionCardToday,
              ]}
              onPress={() => handleExpandQuestion(question.id, question.challengeDate)}
              disabled={state === 'locked'}
              activeOpacity={state === 'locked' ? 1 : 0.7}
            >
              {/* Header */}
              <View style={styles.questionHeader}>
                <View style={styles.dayBadge}>
                  <Text style={[
                    styles.dayText,
                    state === 'locked' && styles.dayTextLocked
                  ]}>
                   {t('quiz.dayTitle', { day: quizDayLabel(question.challengeDate) })}
                  </Text>
                </View>
                <View style={styles.stateContainer}>
                  {getStateIcon(state)}
                  {/* Points à gagner annoncés AVANT de répondre (badge, comme
                      le +10 pts du test d'app). Visible sur toute question
                      répondable — dont tous les jours pour les admins. */}
                  {state === 'available' && (
                    <View style={styles.quizPointsBadge}>
                      <Text style={styles.quizPointsBadgeText}>
                        {t('common.plusPointsShort', { points: question.points })}
                      </Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Expanded content */}
              {isExpanded && (
                <View style={styles.expandedContent}>
                  <Text style={styles.questionText}>{question.question}</Text>

                  {/* Answers — options mélangées côté serveur ; coloration
                      uniquement après réponse, à partir de la correction
                      renvoyée par le serveur. */}
                  <View style={styles.answersContainer}>
                    {question.options.map((answerText, index) => {
                      const isSelected = selectedAnswer === answerText;
                      const answered = showFeedback || !!answer;
                      // Réponse choisie : état local (session courante) ou
                      // correction serveur (jour passé rechargé).
                      const pickedText = selectedAnswer ?? correction?.selectedAnswer;
                      const showCorrect =
                        answered && !!correction && answerText === correction.correctAnswer;
                      const showIncorrect =
                        answered &&
                        !!correction &&
                        answerText === pickedText &&
                        answerText !== correction.correctAnswer;

                      return (
                        <TouchableOpacity
                          key={index}
                          style={[
                            styles.answerOption,
                            isSelected && !showFeedback && !answer && styles.answerSelected,
                            showCorrect && styles.answerCorrect,
                            showIncorrect && styles.answerIncorrect,
                          ]}
                          onPress={() => !showFeedback && !answer && setSelectedAnswer(answerText)}
                          disabled={showFeedback || !!answer}
                        >
                          <Text
                            style={[
                              styles.answerText,
                              isSelected && !showFeedback && !answer && styles.answerTextSelected,
                              showCorrect && styles.answerTextCorrect,
                              showIncorrect && styles.answerTextIncorrect,
                            ]}
                          >
                            {answerText}
                          </Text>
                          {showCorrect && (
                            <Ionicons name="checkmark-circle" size={20} color="#83BF4F" />
                          )}
                          {showIncorrect && (
                            <Ionicons name="close-circle" size={20} color="#E74C3C" />
                          )}
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  {/* Feedback */}
                  {(showFeedback || answer) && (
                    <View style={styles.feedbackContainer}>
                      <View style={styles.feedbackHeader}>
                        <Ionicons
                          name={(isCorrect || answer?.isCorrect) ? "checkmark-circle" : "close-circle"}
                          size={22}
                          color={(isCorrect || answer?.isCorrect) ? "#2D7A3E" : "#CD7728"}
                        />
                        <Text style={[
                          styles.feedbackTitle, 
                          (isCorrect || answer?.isCorrect) && styles.feedbackTitleCorrect
                        ]}>
                          {(isCorrect || answer?.isCorrect)
                            ? t('quiz.correctFeedback', {
                                points: pointsEarned || answer?.pointsEarned,
                              })
                            : t('quiz.incorrectFeedback')}
                        </Text>
                      </View>
                      {correction?.explanation && (
                        <Text style={styles.feedbackText}>{correction.explanation}</Text>
                      )}
                    </View>
                  )}

                  {/* Validate Button */}
                  {!showFeedback && !answer && (
                    <TouchableOpacity
                      style={styles.validateButton}
                      onPress={handleValidate}
                      disabled={!selectedAnswer || isSubmitting}
                    >
                      {selectedAnswer && !isSubmitting ? (
                        <LinearGradient
                          colors={["#1C453F", "#78A584"]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={styles.validateGradient}
                        >
                          <Text style={styles.validateText}>{t('common.validate')}</Text>
                        </LinearGradient>
                      ) : (
                        <View style={styles.validateDisabled}>
                          <Text style={styles.validateTextDisabled}>
                            {isSubmitting ? "..." : t('common.validate')}
                          </Text>
                        </View>
                      )}
                    </TouchableOpacity>
                  )}

                  {/* App du jour à tester */}
                  {(() => {
                    const dayApp = getTestAppForDate(question.challengeDate);
                    if (!dayApp) return null;
                    return (
                      <DayTestApp
                        app={dayApp}
                        opened={openedAppIds.has(dayApp.id)}
                        tested={testedAppIds.has(dayApp.id)}
                        onOpenApp={() => markOpened(dayApp.id)}
                        onMarkTested={() => handleMarkTested(dayApp.id, dayApp.kind)}
                      />
                    );
                  })()}
                </View>
              )}

             
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {showConfetti && (
        <ConfettiCannon
          key={confettiKey}
          count={120}
          origin={{ x: -10, y: 0 }}
          fadeOut
          autoStart
          onAnimationEnd={() => setShowConfetti(false)}
        />
      )}
    </SafeAreaView>
  );
}

interface DayTestAppProps {
  app: TestApp;
  opened: boolean;
  tested: boolean;
  onOpenApp: () => void;
  onMarkTested: () => Promise<unknown>;
}

function DayTestApp({ app, opened, tested, onOpenApp, onMarkTested }: DayTestAppProps) {
  const { t } = useTranslation();
  const handleOpen = async () => {
    onOpenApp();
    await WebBrowser.openBrowserAsync(getTestAppUrl(app), {
      presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
      toolbarColor: '#FBEFE3',
      controlsColor: '#CD7728',
    });
  };

  const handleMark = async () => {
    if (tested) return;
    try {
      await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch {
      // haptics unavailable
    }
    await onMarkTested();
  };

  const showConfirmButton = opened || tested;

  return (
    <View style={styles.dayAppContainer}>
      {/* M1 : les points à gagner sont annoncés AVANT le test, pas seulement
          dans l'alerte de confirmation. */}
      <View style={styles.dayAppHeaderRow}>
        <Text style={styles.dayAppHeader}>
          {t(app.kind === 'site' ? 'quiz.siteToDiscover' : 'quiz.appToTest')}
        </Text>
        <View style={styles.dayAppPointsBadge}>
          <Text style={styles.dayAppPointsBadgeText}>
            {t('common.plusPointsShort', { points: TEST_APP_POINTS })}
          </Text>
        </View>
      </View>

      {/* Card entièrement cliquable : ouvre l'app dans un navigateur in-app */}
      <TouchableOpacity
        style={styles.dayAppCard}
        onPress={handleOpen}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={t('quiz.openAppA11y', { name: app.name })}
      >
        <View style={styles.dayAppMain}>
          <View style={[styles.dayAppEmojiBubble, { backgroundColor: `${app.color}22` }]}>
            <Text style={styles.dayAppEmoji}>{app.emoji}</Text>
          </View>
          <View style={styles.dayAppText}>
            <Text style={styles.dayAppName}>{app.name}</Text>
            <Text style={styles.dayAppTagline} numberOfLines={2}>
              {t(app.taglineKey)}
            </Text>
          </View>
          <Ionicons name="open-outline" size={18} color="#999" />
        </View>

        {/* Le bouton "J'ai testé" n'apparaît qu'après avoir ouvert l'app
            (= utilisateur est revenu de la WebBrowser). Reste affiché en
            mode "Déjà testée" une fois confirmé en DB. */}
        {showConfirmButton && (
          <TouchableOpacity
            style={[styles.dayAppButton, tested && styles.dayAppButtonDone]}
            onPress={handleMark}
            disabled={tested}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityState={{ disabled: tested, selected: tested }}
            accessibilityLabel={
              tested
                ? t('quiz.alreadyTestedA11y', { name: app.name })
                : t('quiz.markTestedA11y', { name: app.name })
            }
          >
            {tested ? (
              <>
                <Ionicons name="checkmark-circle" size={16} color="#1C453F" />
                <Text style={styles.dayAppButtonDoneText}>
                  {t(app.kind === 'site' ? 'quiz.alreadyVisited' : 'quiz.alreadyTested')}
                </Text>
              </>
            ) : (
              <Text style={styles.dayAppButtonText}>
                {t(app.kind === 'site' ? 'quiz.iVisited' : 'quiz.iTested')}
              </Text>
            )}
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FBEFE3",
    paddingTop: 24,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
    gap: 16,
  },
  emptyTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 24,
    color: "#1C453F",
  },
  emptyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    textAlign: "center",
    lineHeight: 24,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#000",
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  missionsDoneCard: {
    backgroundColor: "#1C453F",
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    alignItems: "center",
  },
  missionsDoneEmoji: {
    fontSize: 32,
    marginBottom: 8,
  },
  missionsDoneTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#FFF",
    marginBottom: 6,
    textAlign: "center",
  },
  missionsDoneText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#FBEFE3",
    textAlign: "center",
    lineHeight: 20,
  },
  questionCard: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  questionCardLocked: {
    backgroundColor: "#fff",
  },
  questionCardToday: {
    // No border - kept for semantic styling if needed later
  },
  questionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dayBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dayText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#000",
  },
  dayTextLocked: {
    color: "#D4D6DD",
  },
  todayBadge: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 10,
    color: "#FFF",
    backgroundColor: "#CD7728",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    overflow: "hidden",
  },
  stateContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  bonusPointsText: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#733801",
  },
  pointsBadgeText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 12,
    color: "#CD7728",
  },
  pointsBadgeTextLocked: {
    color: "#999",
  },
  lockedText: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#999",
    marginTop: 8,
  },
  expandedContent: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#F0F0F0",
    paddingTop: 16,
  },
  questionText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#000",
    lineHeight: 24,
    marginBottom: 16,
  },
  answersContainer: {
    gap: 10,
  },
  answerOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: "#733801",
  },
  answerSelected: {
    backgroundColor: "#E8F4FD",
    borderColor: "#1C453F",
  },
  answerCorrect: {
    backgroundColor: "#E8F5E9",
    borderColor: "#83BF4F",
  },
  answerIncorrect: {
    backgroundColor: "#FFEBEE",
    borderColor: "#E74C3C",
  },
  answerText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#333",
    flex: 1,
  },
  answerTextSelected: {
    fontFamily: "Inter_600SemiBold",
    color: "#1C453F",
  },
  answerTextCorrect: {
    fontFamily: "Inter_600SemiBold",
    color: "#83BF4F",
  },
  answerTextIncorrect: {
    color: "#E74C3C",
  },
  feedbackContainer: {
    marginTop: 16,
    padding: 14,
    backgroundColor: "#FFF9F0",
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#733801",
  },
  feedbackHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  feedbackTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#733801",
  },
  feedbackTitleCorrect: {
    color: "#2D7A3E",
  },
  feedbackText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 20,
    flex: 1,
  },
  feedbackContainerCorrect: {
    backgroundColor: "#E8F5E9",
    borderLeftColor: "#83BF4F",
  },
  feedbackContainerIncorrect: {
    backgroundColor: "#FFF3E0",
    borderLeftColor: "#CD7728",
  },
  feedbackEmoji: {
    fontSize: 28,
  },
  feedbackHeaderText: {
    flex: 1,
  },
  feedbackPoints: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 13,
    color: "#83BF4F",
    marginTop: 2,
  },
  feedbackExplanation: {
    flexDirection: "row",
    gap: 8,
    backgroundColor: "#FFF",
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
  },
  alreadyAnsweredBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginTop: 12,
    alignSelf: "flex-start",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  validateButton: {
    marginTop: 16,
    borderRadius: 12,
    overflow: "hidden",
  },
  validateGradient: {
    paddingVertical: 14,
    alignItems: "center",
  },
  validateText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  validateDisabled: {
    backgroundColor: "#D4D6DD",
    paddingVertical: 14,
    alignItems: "center",
    borderRadius: 12,
  },
  validateTextDisabled: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
  },
  alreadyAnsweredText: {
    fontFamily: "Inter_500Medium",
    fontSize: 12,
    color: "#666",
  },
  dayAppContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#F0E8DC",
  },
  dayAppHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  dayAppHeader: {
    fontFamily: "Inter_700Bold",
    fontSize: 13,
    color: "#1C453F",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  quizPointsBadge: {
    backgroundColor: "#FCC189",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  quizPointsBadgeText: {
    fontFamily: "Inter_700Bold",
    fontSize: 12,
    color: "#733801",
  },
  dayAppPointsBadge: {
    backgroundColor: "#FCC189",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  dayAppPointsBadgeText: {
    fontFamily: "Inter_700Bold",
    fontSize: 11,
    color: "#733801",
  },
  dayAppCard: {
    backgroundColor: "#FBEFE3",
    borderRadius: 12,
    padding: 12,
  },
  dayAppMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  dayAppEmojiBubble: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  dayAppEmoji: {
    fontSize: 20,
  },
  dayAppText: {
    flex: 1,
  },
  dayAppName: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#000",
    marginBottom: 2,
  },
  dayAppTagline: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#666",
    lineHeight: 16,
  },
  dayAppButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    marginTop: 10,
    paddingVertical: 9,
    borderRadius: 10,
    backgroundColor: "#1C453F",
  },
  dayAppButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 13,
    color: "#FFF",
  },
  dayAppButtonDone: {
    backgroundColor: "#E8F1EC",
  },
  dayAppButtonDoneText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 13,
    color: "#1C453F",
  },
});
